import { Volume2 } from 'lucide-react';
import { canSpeak, speakElementName } from './elementSpeech';

/**
 * The big element tile shown in a periodic table's detail card, plus the
 * pronunciation control.
 *
 * Instructor: "Also symol needs to be bigger so we can see the atomic number
 * and mass and these changes should be applied to any periodic table thats
 * presented this way."
 *
 * The old tile was 64px square with the atomic number at 10px and the mass at
 * EIGHT. Eight-pixel text is not small, it is unreadable — and the mass is one
 * of the two numbers the slide is teaching a student to find. This is one
 * component precisely so the two periodic tables cannot drift apart again:
 * InteractivePeriodicTable and PeriodicTablePro both render it.
 *
 * Pronunciation uses the device's own voice and costs nothing per press — see
 * elementSpeech.ts for why that, and not the paid TTS path.
 */
export interface ElementFaceProps {
  z: number;
  symbol: string;
  name: string;
  mass: number;
  /** Tile background, from the table's own category palette. */
  background: string;
  /** Ink colours the caller already computes for contrast on `background`. */
  ink: string;
  inkSoft: string;
}

export default function ElementFace({ z, symbol, name, mass, background, ink, inkSoft }: ElementFaceProps) {
  const speechAvailable = canSpeak();

  return (
    <div className="flex flex-shrink-0 flex-col items-center gap-1.5">
      <div
        className="flex h-24 w-24 flex-col items-center justify-center rounded-md px-1"
        style={{ backgroundColor: background }}
      >
        {/* Atomic number: 10px -> 13px, and it keeps its own line. */}
        <span className="self-start text-[13px] font-semibold leading-none tabular-nums" style={{ color: inkSoft }}>
          {z}
        </span>
        <span className="text-4xl font-bold leading-none" style={{ color: ink }}>
          {symbol}
        </span>
        {/* Mass: 8px -> 12px. This is the number the "Inside a Box" slide
            teaches students to read, so it has to be legible on a projector at
            the back of a room, not only on a laptop at arm's length. */}
        <span className="mt-1 text-[12px] font-medium leading-none tabular-nums" style={{ color: inkSoft }}>
          {mass.toFixed(2)}
        </span>
      </div>

      {/* Hidden, not disabled, where the browser offers no speech: a silent
          play button teaches a student that the feature is broken. */}
      {speechAvailable && (
        <button
          type="button"
          onClick={() => speakElementName(name)}
          className="inline-flex items-center gap-1 rounded-md border border-ws-border bg-ws-surface px-2 py-1 text-[11px] font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-accent"
          aria-label={`Hear how ${name} is pronounced`}
          title={`Hear how ${name} is pronounced`}
        >
          <Volume2 size={12} aria-hidden="true" />
          Say it
        </button>
      )}
    </div>
  );
}
